/**
 * Registering a program.
 *
 * Registering records that a program exists; it does not import anything.
 * Those are separate acts on the back end for a reason - the first must not
 * fail because the second would - and the dialog says so rather than leaving
 * an empty model looking like a failure.
 *
 * The slug is the URL segment and the directory name, so it is derived from
 * the display name and then left editable: typing it twice is the commonest
 * way to get the two out of step.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError } from '../../api'
import { useProject } from '../../state/Project'

/** A display name reduced to something usable as a path and a URL segment. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

export default function CreateProjectDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated?: (slug: string) => void
}) {
  const { create } = useProject()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>('')

  const effectiveSlug = slugEdited ? slug : slugify(name)

  const reset = () => {
    setName('')
    setSlug('')
    setSlugEdited(false)
    setDescription('')
    setError('')
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const created = await create({ slug: effectiveSlug, name: name.trim(), description })
      reset()
      onCreated?.(created.slug)
      onClose()
    } catch (failure) {
      setError(
        failure instanceof ApiError ? failure.message : 'Could not register the program.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New program</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Registers the program and switches you to it. Its model is imported
          separately, so it starts empty.
        </DialogContentText>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="BALROG"
            autoFocus
            fullWidth
          />
          <TextField
            label="Short name"
            value={effectiveSlug}
            onChange={(event) => {
              setSlugEdited(true)
              setSlug(slugify(event.target.value))
            }}
            helperText="Used in URLs and as the directory holding its .sysml files"
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          variant="contained"
          disabled={busy || !effectiveSlug}
        >
          Register
        </Button>
      </DialogActions>
    </Dialog>
  )
}
